import {
	Spinner,
	Stack,
	DefaultButton,
	StackItem,
	Pivot,
	PivotItem,
	SpinnerSize
} from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { IUserConfigPackage } from '../../../API';
import { useEffect } from 'react';
import {
	EntityAPI,
	EntityType,
	IEntityQueryResult,
	IEntityTypeDefinition,
	ProvisionType,
	SecurityMode,
	SharingCapabilities
} from '../../../API';
import EntityTypeEditorDialog, { EntityTypeEditorState } from './EntityTypeEditor';
import { ReusableDialogState } from '../../controls/ReusableDialog';
import EntityTypeCreateNewDialog from './EntityTypeCreateNewDialog';

export class EntityTypesState {
	public selectedEntityTypeIndex: string = '0';
	setSelectedEntityTypeIndex(arg0: string) {
		this.selectedEntityTypeIndex = arg0;
	}
	
	public userConfig: IUserConfigPackage;
	public entityTypeStates?: EntityTypeEditorState[];
	public loading = false;

	public newEntityTypeDialog: ReusableDialogState;
	
	constructor(userConfig: IUserConfigPackage) {
		this.userConfig = userConfig;
		this.newEntityTypeDialog = new ReusableDialogState("Create new list connection", 400);
		makeAutoObservable(this);
	}

	private async getApi() : Promise<EntityAPI<IEntityTypeDefinition>> {
		return await EntityAPI.fromEntityType<IEntityTypeDefinition>(
			EntityType.EntityTypeDefinition,
			this.userConfig,
		);
	}

	public loadItems = async (): Promise<void> => {
		this.loading = true;
		let api = await this.getApi();
		let items = await api.all();
		this.setEntityTypes(items);
		this.loading = false;
	};

	ensureInitialLoaded() {
		if (!this.entityTypeStates) {
			this.loadItems();
		}
	}
	
	setEntityTypes(items: IEntityQueryResult<IEntityTypeDefinition>) {
		let entityTypes = items.items.filter(i => (Number(i.Type) || EntityType.None) === EntityType.None);
		this.entityTypeStates = entityTypes.map(et => {
			let state = new EntityTypeEditorState(
			et,
			this.userConfig,
			() => {
				this.entityTypeStates = this.entityTypeStates?.filter(ets => ets !== state);
			});

			return state;
		});
	}
	
	public newEntityTypeListUrl:string = '';
	public setNewEntityTypeListUrl(v:string) {
		this.newEntityTypeListUrl = v;
	}
	public newEntityUrlStorageField:string = '';
	public setNewEntityUrlStorageField(v:string) {
		this.newEntityUrlStorageField = v;
	}
	public newEntityTypeProvisionType:ProvisionType = ProvisionType.Site;
	public setNewEntityTypeProvisionType(v:ProvisionType) {
		this.newEntityTypeProvisionType = v;
	}
	public newEntityTypeSingularName: string = '';
	public setNewEntityTypeSingularName(v:string) {
		this.newEntityTypeSingularName = v;
	}
	public newEntityTypePluralName: string = '';
	public setNewEntityTypePluralName(v:string) {
		this.newEntityTypePluralName = v;
	}

	public showAddEntityDialog(visible: boolean) {
		this.newEntityTypeListUrl = '';
		this.newEntityUrlStorageField = '';
		this.newEntityTypePluralName = '';
		this.newEntityTypeSingularName = '';
		this.newEntityTypeProvisionType = ProvisionType.Site;
		this.newEntityTypeDialog.setVisible(visible);
	}

	public addNewEntityType() {
		this.confirmAddNewEntityType(this.newEntityTypeSingularName, this.newEntityTypePluralName, this.newEntityTypeListUrl, this.newEntityUrlStorageField, this.newEntityTypeProvisionType);
		this.newEntityTypeDialog.setVisible(false);
	}

	public confirmAddNewEntityType(singularName:string, pluralName:string, listUrl:string, urlStorageField:string, type:ProvisionType) {

		const entityType: IEntityTypeDefinition = {
			Id: '',
			DisplayTitle: '',
			ServerRelativeUrl: '',
			NativeMetadataUrl: '',
			SingularName: singularName,
			PluralName: pluralName,
			SharePointListUrl: listUrl,
			ProvisionProfiles: [],
			SharePointStorageUrlField: urlStorageField,
			SharePointStorageUrlPattern: '',
			SharePointTemplateDocumentsLibraryUrl: '',
			SharePointTemplateApprovalsListUrl: '',
			SharePointTemplateApprovals: false,
			EnableWorkspaceSync: false,
			Type: EntityType.None,
			OutlookFilingTagTemplate: '',
			OutlookIncludeInSearchResults: false,
			OutlookEnableCreateNewOption: false,
			OutlookEnableViewAllOption: false,
			ContentProvisioningIndicatorField: ''
		};

		if(!this.entityTypeStates) {
			this.entityTypeStates = [];
		}

		let state = new EntityTypeEditorState(entityType, this.userConfig, () => {
			this.entityTypeStates = this.entityTypeStates?.filter(ets => ets !== state);
		});
		
		state.profiles.loaded = true; // No need to load profiles as we're creating a brand new entity type
		state.profiles.addNewProfile({
			Id: '',
			Name: 'New workspace profile',
			SiteSharingCapabilities: SharingCapabilities.Disabled,
			SiteTemplate: 'STS#3',
			ProvisionType: type,
			WorkspaceTitle: '{{Item.Title}}',
			WorkspacePath: '',
			WorkspacePathComponent: '{{Item.ID}}',
			OneDriveSyncPath: 'Workspaces/{{Item.Title}}',
			SecurityMode: SecurityMode.None,
			PrivacyExpression: '',
			FavouritesExpression: '',
			MetadataPrivacyExpression: '',
			Expression: '',
			CreateWorkspaceExpression: '',
			TemplateTriggerIds: [],
			TriggerIds: [],
			AIPropertyIds: [],
			SecurityMemberFields: [],
			SecurityOwnerFields: [],
			SecurityVisitorFields: [],
			SecurityMembers: [],
			SecurityOwners: [],
			SecurityVisitors: [],
			EnableOneDriveSync: false,
			MetadataFields: [],
			FavouritesPeopleFields: [],
			FavouritesPeople: [],
			ContentTypeIds: [],
			MoveAttachmentsToWorkspace: true,
			MoveAttachmentsToWorkspacePath: '',
			SensitivityLabelExpression: '',
			MailNickname: '',
			Description: '',
			MetadataSecurityMode: SecurityMode.None,
			MetadataSecurityMemberFields: [],
			MetadataSecurityOwnerFields: [],
			MetadataSecurityVisitorFields: [],
			MetadataSecurityMembers: [],
			MetadataSecurityOwners: [],
			MetadataSecurityVisitors: [],

			//Boring required fields that we should get rid of
			DisplayTitle: '',
			ServerRelativeUrl: '',
			NativeMetadataUrl: '',
		});

		this.entityTypeStates.push(state);
		this.selectedEntityTypeIndex = (this.entityTypeStates.length - 1).toString();
	}
}

function EntityTypes({ state }: { state: EntityTypesState }) {
	useEffect(() => {
		state.ensureInitialLoaded();
	}, [state]);

	if (state.loading || !state.entityTypeStates) {
		return <Spinner size={SpinnerSize.large} />;
	}

	return (<>
			<EntityTypeCreateNewDialog state={state} />
			<Stack>
				<StackItem>
					<Stack horizontal>

						<StackItem grow>
							<div style={{fontSize:22, color:'#959595'}}>Entity Connections</div>
						</StackItem>

						<StackItem>
							<DefaultButton
							style={{marginRight:20}}
								iconProps={{ iconName: 'Add' }}
								text="Add a new entity type..."
								onClick={() => {
									state.showAddEntityDialog(true);
								}}
							/>
						</StackItem>
					</Stack>
				</StackItem>
				<StackItem>

				{ state.entityTypeStates.length === 0 &&
					<div style={{marginTop:20, marginBottom:20, padding:20, backgroundColor:'#f3f2f1', border:'1px solid #e1dfdd', borderRadius:5}}>
						No connections have been configured. Use the button above to add a new list connection.
						<br/><br/>
						Entity connections define which SharePoint list you will use to store configuration for your workspaces, and how those workspaces will be provisioned.
					</div>
				}

				<Pivot
					style={{marginTop:10}}
					selectedKey={state.selectedEntityTypeIndex}
					onLinkClick={(p) => { state.setSelectedEntityTypeIndex(p?.props.itemKey || ''); }}>

					{state.entityTypeStates.map((ets, idx) => (
						<PivotItem headerText={ets.PluralName} itemKey={idx.toString()} key={ets.Id || idx}>
							<EntityTypeEditorDialog state={ets} />
						</PivotItem>
					))}
				</Pivot>

				</StackItem>
			</Stack>
		</>
	);
}

export default observer(EntityTypes);
